// ============================================================================
// HAI Reporting Tool - Reports & Export Page
// Data aggregation, visualization, and export functionality
// ============================================================================

import { useState, useEffect } from 'react';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { InfectionRateChart } from '@/components/dashboard/InfectionRateChart';
import { exportToCSV, exportToPDF, calculatePeriodMetricsFromData } from '@/services/export.service';
import { getDeviceSnapshots, getHAIRecords, getSSISurveillance } from '@/services/firestore.service';
import { db } from '@/services/db';
import type { Ward, PeriodMetrics } from '@/types';
import { formatRate } from '@/utils/calculations';

export function ReportsPage() {
  const [wards, setWards] = useState<Ward[]>([]);
  const [selectedWardIds, setSelectedWardIds] = useState<string[]>([]);
  const [periodStart, setPeriodStart] = useState<string>(() => {
    const date = new Date();
    date.setMonth(date.getMonth() - 1);
    return date.toISOString().split('T')[0];
  });
  const [periodEnd, setPeriodEnd] = useState<string>(() => new Date().toISOString().split('T')[0]);
  const [metrics, setMetrics] = useState<PeriodMetrics[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [facilityName, setFacilityName] = useState('Healthcare Facility');

  // Load wards on mount
  useEffect(() => {
    loadWards();
  }, []);

  async function loadWards() {
    try {
      const wardsFromDb = await db.wards.toArray();
      setWards(wardsFromDb);

      // Pre-select user's facility wards
      if (wardsFromDb.length > 0) {
        setSelectedWardIds([wardsFromDb[0].id]);
      }
    } catch (err) {
      console.error('Failed to load wards:', err);
      setError('Failed to load wards');
    }
  }

  async function generateReport() {
    if (selectedWardIds.length === 0) {
      setError('Please select at least one ward');
      return;
    }

    if (!periodStart || !periodEnd) {
      setError('Please select both start and end dates');
      return;
    }

    if (new Date(periodEnd) < new Date(periodStart)) {
      setError('End date must be after or equal to start date');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const allMetrics: PeriodMetrics[] = [];

      for (const wardId of selectedWardIds) {
        // Fetch data for this ward and period
        const deviceSnapshots = await getDeviceSnapshots(wardId, periodStart, periodEnd);
        const haiRecords = await getHAIRecords(wardId, periodStart, periodEnd);
        const ssiRecords = await getSSISurveillance(wardId, periodStart, periodEnd);

        // Calculate metrics
        const wardMetrics = calculatePeriodMetricsFromData(
          wardId,
          periodStart,
          periodEnd,
          deviceSnapshots,
          haiRecords,
          ssiRecords
        );

        allMetrics.push(wardMetrics);
      }

      setMetrics(allMetrics);
    } catch (err: any) {
      console.error('Failed to generate report:', err);
      setError(err.message || 'Failed to generate report');
    } finally {
      setIsLoading(false);
    }
  }

  function handleWardSelection(wardId: string) {
    setSelectedWardIds((prev) =>
      prev.includes(wardId)
        ? prev.filter((id) => id !== wardId)
        : [...prev, wardId]
    );
  }

  function handleExportCSV() {
    if (metrics.length === 0) {
      alert('Please generate a report first');
      return;
    }

    const filename = `hai-report-${periodStart}-to-${periodEnd}.csv`;
    exportToCSV(metrics, wards, filename);
  }

  function handleExportPDF() {
    if (metrics.length === 0) {
      alert('Please generate a report first');
      return;
    }

    const filename = `hai-report-${periodStart}-to-${periodEnd}.pdf`;
    exportToPDF(metrics, wards, facilityName, filename);
  }

  // Prepare chart data for visualization
  const chartData = metrics.length > 0 ? {
    labels: metrics.map((m) => wards.find((w) => w.id === m.wardId)?.name || m.wardId),
    datasets: [
      {
        label: 'CAUTI Rate (per 1000 catheter-days)',
        data: metrics.map((m) => m.rates.uti),
        borderColor: 'rgb(59, 130, 246)',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        tension: 0.1
      },
      {
        label: 'CLABSI Rate (per 1000 CVP-days)',
        data: metrics.map((m) => m.rates.cabsi),
        borderColor: 'rgb(239, 68, 68)',
        backgroundColor: 'rgba(239, 68, 68, 0.1)',
        tension: 0.1
      },
      {
        label: 'VAP Rate (per 1000 ventilator-days)',
        data: metrics.map((m) => m.rates.vap),
        borderColor: 'rgb(251, 146, 60)',
        backgroundColor: 'rgba(251, 146, 60, 0.1)',
        tension: 0.1
      }
    ]
  } : null;

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="card">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">HAI Surveillance Reports</h1>
          <p className="text-sm text-gray-600">
            Generate comprehensive infection rate reports with CSV and PDF export options
          </p>
        </div>

        {/* Filters */}
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Report Parameters</h2>

          <div className="space-y-4">
            {/* Facility Name */}
            <div>
              <label htmlFor="facilityName" className="block text-sm font-medium text-gray-700 mb-1">
                Facility Name
              </label>
              <input
                type="text"
                id="facilityName"
                value={facilityName}
                onChange={(e) => setFacilityName(e.target.value)}
                className="input"
                placeholder="Healthcare Facility"
              />
            </div>

            {/* Date Range */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="periodStart" className="block text-sm font-medium text-gray-700 mb-1">
                  Period Start
                </label>
                <input
                  type="date"
                  id="periodStart"
                  value={periodStart}
                  onChange={(e) => setPeriodStart(e.target.value)}
                  className="input"
                />
              </div>
              <div>
                <label htmlFor="periodEnd" className="block text-sm font-medium text-gray-700 mb-1">
                  Period End
                </label>
                <input
                  type="date"
                  id="periodEnd"
                  value={periodEnd}
                  onChange={(e) => setPeriodEnd(e.target.value)}
                  className="input"
                />
              </div>
            </div>

            {/* Ward Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Wards (select one or more)
              </label>
              <div className="border border-gray-300 rounded-lg p-3 max-h-48 overflow-y-auto">
                {wards.length === 0 ? (
                  <p className="text-sm text-gray-500">No wards available. Please add wards in the Admin page.</p>
                ) : (
                  <div className="space-y-2">
                    {wards.map((ward) => (
                      <label key={ward.id} className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-2 rounded">
                        <input
                          type="checkbox"
                          checked={selectedWardIds.includes(ward.id)}
                          onChange={() => handleWardSelection(ward.id)}
                          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                        />
                        <span className="text-sm text-gray-900">{ward.name}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {selectedWardIds.length} ward(s) selected
              </p>
            </div>

            {/* Error Display */}
            {error && (
              <div className="bg-danger-50 border border-danger-200 rounded-lg p-3">
                <p className="text-sm text-danger-800">{error}</p>
              </div>
            )}

            {/* Generate Button */}
            <button
              onClick={generateReport}
              disabled={isLoading || selectedWardIds.length === 0}
              className="btn btn-primary w-full md:w-auto"
            >
              {isLoading ? 'Generating Report...' : 'Generate Report'}
            </button>
          </div>
        </div>

        {/* Results */}
        {metrics.length > 0 && (
          <>
            {/* Export Buttons */}
            <div className="card">
              <div className="flex flex-wrap gap-3">
                <button onClick={handleExportCSV} className="btn btn-secondary">
                  📄 Export to CSV
                </button>
                <button onClick={handleExportPDF} className="btn btn-secondary">
                  📑 Export to PDF
                </button>
              </div>
            </div>

            {/* Chart Visualization */}
            {chartData && (
              <InfectionRateChart
                data={chartData}
                title={`Infection Rates: ${periodStart} to ${periodEnd}`}
              />
            )}

            {/* Metrics Table */}
            <div className="card overflow-x-auto">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Detailed Metrics</h2>
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ward</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">CVP Days</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cath Days</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Vent Days</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">CLABSI</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">CAUTI</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">VAP</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">CLABSI Rate</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">CAUTI Rate</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">VAP Rate</th>
                    {metrics.some(m => m.ssiRate) && (
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">SSI Rate</th>
                    )}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {metrics.map((metric) => {
                    const ward = wards.find((w) => w.id === metric.wardId);
                    return (
                      <tr key={metric.wardId} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">{ward?.name || metric.wardId}</td>
                        <td className="px-4 py-3 text-sm text-gray-700">{metric.deviceDays.cvp}</td>
                        <td className="px-4 py-3 text-sm text-gray-700">{metric.deviceDays.urinaryCatheter}</td>
                        <td className="px-4 py-3 text-sm text-gray-700">{metric.deviceDays.ventilator}</td>
                        <td className="px-4 py-3 text-sm text-gray-700">{metric.cases.cabsi}</td>
                        <td className="px-4 py-3 text-sm text-gray-700">{metric.cases.uti}</td>
                        <td className="px-4 py-3 text-sm text-gray-700">{metric.cases.vap}</td>
                        <td className="px-4 py-3 text-sm text-gray-700">{formatRate(metric.rates.cabsi)}</td>
                        <td className="px-4 py-3 text-sm text-gray-700">{formatRate(metric.rates.uti)}</td>
                        <td className="px-4 py-3 text-sm text-gray-700">{formatRate(metric.rates.vap)}</td>
                        {metrics.some(m => m.ssiRate) && (
                          <td className="px-4 py-3 text-sm text-gray-700">
                            {metric.ssiRate ? `${formatRate(metric.ssiRate.rate)}%` : '-'}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Summary Statistics */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="card bg-primary-50">
                <h3 className="text-sm font-medium text-primary-900 mb-1">Total Device Days</h3>
                <p className="text-2xl font-bold text-primary-900">
                  {metrics.reduce((sum, m) => sum + m.deviceDays.cvp + m.deviceDays.urinaryCatheter + m.deviceDays.ventilator, 0)}
                </p>
              </div>
              <div className="card bg-danger-50">
                <h3 className="text-sm font-medium text-danger-900 mb-1">Total HCAI Cases</h3>
                <p className="text-2xl font-bold text-danger-900">
                  {metrics.reduce((sum, m) => sum + m.cases.uti + m.cases.cabsi + m.cases.bsiOther + m.cases.vap, 0)}
                </p>
              </div>
              <div className="card bg-warning-50">
                <h3 className="text-sm font-medium text-warning-900 mb-1">Avg CLABSI Rate</h3>
                <p className="text-2xl font-bold text-warning-900">
                  {formatRate(metrics.reduce((sum, m) => sum + m.rates.cabsi, 0) / metrics.length)}
                </p>
              </div>
              <div className="card bg-success-50">
                <h3 className="text-sm font-medium text-success-900 mb-1">Wards Monitored</h3>
                <p className="text-2xl font-bold text-success-900">{metrics.length}</p>
              </div>
            </div>
          </>
        )}

        {/* Empty State */}
        {metrics.length === 0 && !isLoading && (
          <div className="card text-center py-12">
            <div className="text-6xl mb-4">📊</div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Report Generated</h3>
            <p className="text-sm text-gray-600 mb-4">
              Select wards and date range above, then click "Generate Report" to view metrics
            </p>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
